import React from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import StandardButton from '@atlaskit/button/standard-button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import Button from '@atlassian/jira-portfolio-3-common/src/button/index.tsx';
import {
	getPlanId,
	getScenarioId,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/util/urls';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { buildRemoveIssuePath } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import { useRouterActions } from '@atlassian/jira-router';
import messages from './messages';
import type { Props } from './types';

const RemoveIssuesDialog = ({ toggleModalDialog, bulkActionSuccess, removeFromPlan }: Props) => {
	const { push } = useRouterActions();
	const { formatMessage } = useIntl();
	const onClickRemovedIssue = () => {
		const planId = getPlanId();
		const scenarioId = getScenarioId();
		if (isDefined(planId) && isDefined(scenarioId)) {
			push(buildRemoveIssuePath(planId, scenarioId));
		}
	};

	return (
		<ShortcutScope>
			<ModalDialog shouldScrollInViewport onClose={toggleModalDialog} autoFocus={false}>
				<ModalHeader>
					<ModalTitle appearance="danger">
						{formatMessage(
							fg('plans_remove_and_delete_issue_from_timeline')
								? messages.removeFromPlanTitle
								: messages.removeFromPlanTitleOld,
						)}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{fg('plans_remove_and_delete_issue_from_timeline') ? (
						<Text>
							<FormattedMessage
								{...messages.removeIssueFromPlanMessage}
								values={{
									b: (chunks) => <b>{chunks}</b>,
								}}
							/>
						</Text>
					) : (
						/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */
						<span>
							<FormattedMessage
								{...messages.removeIssueFromPlanMessageOld}
								values={{
									removedIssuesPageLink: (
										<Button onClick={onClickRemovedIssue} appearance="link" spacing="none">
											{formatMessage(messages.removedIssueSettingsLinkMessage)}
										</Button>
									),
								}}
							/>
						</span>
					)}
				</ModalBody>
				<ModalFooter>
					<StandardButton appearance="subtle" onClick={toggleModalDialog}>
						{formatMessage(commonMessages.cancel)}
					</StandardButton>
					<StandardButton
						appearance="primary"
						onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
							removeFromPlan();
							bulkActionSuccess(analyticsEvent);
						}}
					>
						{formatMessage(messages.removeFromPlanActionButton)}
					</StandardButton>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
};

export default RemoveIssuesDialog;
